import {
    createContext,
    useEffect,
    useState,
    useMemo,
    useReducer,
    useCallback,
    useRef,
    type ReactNode,
} from 'react'
import { Agent } from '@atproto/api'
import { OAuthSession } from '@atproto/oauth-client-browser'
import {
    initAllSessions,
    login as authLogin,
    logout as authLogout,
    reconnect as authReconnect,
    requestPersistentStorage,
    restoreSession,
    subscribeSessionExpired,
    subscribeSessionRestored,
    getStoredDids,
    removeStoredDid,
    type SessionExpiredEvent,
} from '@/lib/auth'
import { buildExpiredAccountFromCache } from '@/lib/accountUtils'
import type { ExpiredAccount } from '@/types/account'
import { strings } from '@/strings'
import {
    type MastodonSession,
    login as mastodonLogin,
    getStoredSessions as getMastodonSessions,
    removeStoredSession as removeMastodonStoredSession,
    addStoredSession as addMastodonStoredSession,
    getMastodonAccountId,
} from '@/lib/mastodon'
import {
    type PixelfedSession,
    login as pixelfedLogin,
    getStoredSession as getPixelfedSession,
    clearSession as clearPixelfedSession,
} from '@/lib/pixelfed'
import { nip19 } from 'nostr-tools'
import {
    getStoredSession as getNostrSession,
    saveSession as saveNostrSession,
    clearSession as clearNostrSession,
} from '@/lib/nostr'
import type { NostrSession } from '@/types/nostr'
import { fetchNostrProfile } from '@/adapters/nostrAdapter'
import { PROFILE_CACHE_PREFIX, getProfileCacheKey } from '@/constants/profileCache'
import { clearPersistedCache } from '@/lib/queryPersister'
import { queryClient } from '@/lib/queryClient'
import { evictMastodonServerSession } from '@/services/mastodonBioService'
import { unregisterPushForAccount } from '@/services/pushNotificationsService'

const SELECTED_BLUESKY_DID_KEY = 'mackinac:selectedBlueskyDid'
const SELECTED_MASTODON_ID_KEY = 'mackinac:selectedMastodonId'

const E2E_BLUESKY_DID_KEY = 'mackinac:e2e:bluesky_did'
const E2E_EXPIRED_DIDS_KEY = 'mackinac:e2e:bluesky_expired_dids'
const E2E_RECONNECT_DID_KEY = 'mackinac:e2e:reconnect_did'

/**
 * How long to wait before acting on a cross-tab revoke. Another tab's reconnect
 * revokes the old session before storing the refreshed one, and both events are
 * broadcast, so a revoke is provisional until the session store is re-checked.
 */
export const REVOKE_COALESCE_MS = 10_000

// ---------------------------------------------------------------------------
// Bluesky slice — a DID-keyed store plus the stored-DID order, so an account
// keeps its exact switcher position across expiry and reconnect.
// ---------------------------------------------------------------------------

type LiveEntry = { status: 'live'; session: OAuthSession }
type ExpiredEntry = { status: 'expired'; account: ExpiredAccount }
type BlueskyEntry = LiveEntry | ExpiredEntry

interface BlueskyState {
    order: string[]
    entries: Record<string, BlueskyEntry>
}

type BlueskyAction =
    | { type: 'INIT_SNAPSHOT'; live: OAuthSession[]; expiredDids: string[]; order: string[] }
    | { type: 'EXPIRED'; did: string; order: string[] }
    | { type: 'RESTORED'; did: string; session: OAuthSession; order: string[] }
    | { type: 'REMOVED'; did: string; order: string[] }

const EMPTY_BLUESKY_STATE: BlueskyState = { order: [], entries: {} }

/**
 * The stored-DID order is authoritative, but any account we actually hold that
 * storage doesn't know about (E2E injection) is appended rather than dropped.
 */
function reconcileOrder(order: string[], entries: Record<string, BlueskyEntry>): string[] {
    const seen = new Set<string>()
    const out: string[] = []
    for (const did of order) {
        if (!seen.has(did)) {
            seen.add(did)
            out.push(did)
        }
    }
    for (const did of Object.keys(entries)) {
        if (!seen.has(did)) {
            seen.add(did)
            out.push(did)
        }
    }
    return out
}

function blueskyReducer(state: BlueskyState, action: BlueskyAction): BlueskyState {
    switch (action.type) {
        case 'INIT_SNAPSHOT': {
            const entries: Record<string, BlueskyEntry> = {}
            for (const session of action.live) {
                entries[session.did] = { status: 'live', session }
            }
            for (const did of action.expiredDids) {
                if (!entries[did]) {
                    entries[did] = { status: 'expired', account: buildExpiredAccountFromCache(did) }
                }
            }
            return { order: reconcileOrder(action.order, entries), entries }
        }
        case 'EXPIRED': {
            const existing = state.entries[action.did]
            if (existing?.status === 'expired') return state
            // Ignore DIDs that are neither ours nor stored (another app's broadcast)
            if (!existing && !action.order.includes(action.did)) return state
            const entries = {
                ...state.entries,
                [action.did]: {
                    status: 'expired' as const,
                    account: buildExpiredAccountFromCache(action.did),
                },
            }
            return { order: reconcileOrder(action.order, entries), entries }
        }
        case 'RESTORED': {
            const existing = state.entries[action.did]
            if (!existing && !action.order.includes(action.did)) return state
            const entries = {
                ...state.entries,
                [action.did]: { status: 'live' as const, session: action.session },
            }
            return { order: reconcileOrder(action.order, entries), entries }
        }
        case 'REMOVED': {
            if (!state.entries[action.did]) return state
            const entries = { ...state.entries }
            delete entries[action.did]
            return {
                order: reconcileOrder(
                    action.order.filter(d => d !== action.did),
                    entries
                ),
                entries,
            }
        }
    }
}

function readE2EExpiredDids(): string[] {
    try {
        const raw = localStorage.getItem(E2E_EXPIRED_DIDS_KEY)
        if (!raw) return []
        const parsed: unknown = JSON.parse(raw)
        return Array.isArray(parsed) ? parsed.filter((d): d is string => typeof d === 'string') : []
    } catch {
        return []
    }
}

/** Drop the E2E seed keys for a DID so a reload doesn't resurrect a removed account. */
function e2eForgetBlueskyDid(did: string): void {
    try {
        const remaining = readE2EExpiredDids().filter(d => d !== did)
        localStorage.setItem(E2E_EXPIRED_DIDS_KEY, JSON.stringify(remaining))
        if (localStorage.getItem(E2E_BLUESKY_DID_KEY) === did) {
            localStorage.removeItem(E2E_BLUESKY_DID_KEY)
        }
    } catch {
        // localStorage unavailable
    }
}

/**
 * Mirrors the DID of the Bluesky session currently active in the app. Kept
 * current by an effect in `AuthProvider` below — which, per `main.tsx`, is
 * mounted for the entire app lifetime and never unmounts on route navigation
 * — rather than by any individual page-level hook. A deferred write (e.g. the
 * pin-confirm poll in `usePostEngagement`, which can resolve up to ~56s after
 * it started) needs to know who's active *right now*, and that can change on
 * a route where the hook that kicked off the write isn't mounted (e.g.
 * switching accounts from Settings). Reading this getter instead of
 * maintaining a per-hook mirror means the answer is always current
 * regardless of which components happen to be mounted.
 */
let activeBlueskySessionDid: string | null = null

export function getActiveBlueskySessionDid(): string | null {
    return activeBlueskySessionDid
}

export interface AuthContextType {
    // All live sessions (for building connected accounts list)
    sessions: OAuthSession[]
    /** Bluesky accounts kept as signed-out, reconnectable placeholders. */
    expiredAccounts: ExpiredAccount[]
    /** Stored-DID order covering both live and expired Bluesky accounts. */
    blueskyDidOrder: string[]
    mastodonSessions: MastodonSession[]
    // Active sessions (for hooks — backward compatible)
    session: OAuthSession | null
    mastodonSession: MastodonSession | null
    pixelfedSession: PixelfedSession | null
    nostrSession: NostrSession | null
    isLoading: boolean
    isAuthenticated: boolean
    // Session management
    login: (handle: string) => Promise<void>
    loginMastodon: (instanceUrl: string) => Promise<void>
    removeAccount: (accountId: string) => Promise<void>
    setActiveBlueskyDid: (did: string) => void
    setActiveMastodonId: (id: string) => void
    loginPixelfed: (instanceUrl: string) => Promise<void>
    logoutPixelfed: () => Promise<void>
    loginNostr: (method: 'nip07' | 'nsec', nsec?: string) => Promise<void>
    logoutNostr: () => void
    showNostrReauth: boolean
    requestNostrReauth: () => Promise<boolean>
    resolveNostrReauth: (success: boolean) => void
    totalAccountCount: number
    updateMastodonSessionAccount: (accountId: string, account: MastodonSession['account']) => void
    /** Start a DID-pinned reconnect. Resolves false when one is already in flight. */
    reconnectBluesky: (did: string) => Promise<boolean>
    /** Durable message from a failed reconnect — cleared only by clearReconnectError(). */
    reconnectError: string | null
    clearReconnectError: () => void
}

/**
 * Fetch and cache Bluesky profile info for sessions that don't have cached data.
 * This ensures the account switcher shows display names instead of raw DIDs.
 */
async function ensureBlueskyProfilesCached(sessions: OAuthSession[]): Promise<void> {
    await Promise.allSettled(
        sessions.map(async s => {
            const cacheKey = `${PROFILE_CACHE_PREFIX}bluesky:${s.did}`
            if (localStorage.getItem(cacheKey)) return
            try {
                const agent = new Agent(s)
                const res = await agent.app.bsky.actor.getProfile({ actor: s.did })
                localStorage.setItem(
                    cacheKey,
                    JSON.stringify({
                        displayName: res.data.displayName ?? res.data.handle,
                        handle: res.data.handle,
                        avatar: res.data.avatar,
                    })
                )
            } catch {
                // Profile fetch failed — account switcher will fall back to DID
            }
        })
    )
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
    const [blueskyState, dispatch] = useReducer(blueskyReducer, EMPTY_BLUESKY_STATE)
    const [mastodonSessions, setMastodonSessions] = useState<MastodonSession[]>([])
    const [pixelfedSession, setPixelfedSession] = useState<PixelfedSession | null>(null)
    const [nostrSession, setNostrSession] = useState<NostrSession | null>(() => getNostrSession())
    const [selectedBlueskyDid, setSelectedBlueskyDid] = useState<string | null>(() => {
        try {
            return localStorage.getItem(SELECTED_BLUESKY_DID_KEY)
        } catch {
            return null
        }
    })
    const [selectedMastodonId, setSelectedMastodonId] = useState<string | null>(() => {
        try {
            return localStorage.getItem(SELECTED_MASTODON_ID_KEY)
        } catch {
            return null
        }
    })
    const [isLoading, setIsLoading] = useState(true)
    const [showNostrReauth, setShowNostrReauth] = useState(false)
    const [reconnectError, setReconnectError] = useState<string | null>(null)
    const reauthResolverRef = useRef<((success: boolean) => void) | null>(null)

    // Derived Bluesky views — walking `order` keeps switcher positions stable.
    const sessions = useMemo(
        () =>
            blueskyState.order.flatMap(did => {
                const entry = blueskyState.entries[did]
                return entry?.status === 'live' ? [entry.session] : []
            }),
        [blueskyState]
    )
    const expiredAccounts = useMemo(
        () =>
            blueskyState.order.flatMap(did => {
                const entry = blueskyState.entries[did]
                return entry?.status === 'expired' ? [entry.account] : []
            }),
        [blueskyState]
    )
    const blueskyDidOrder = blueskyState.order

    // ------------------------------------------------------------------
    // Refs mirroring state, so the expiry/restore coordinator sees the
    // latest values without waiting for a React commit.
    // ------------------------------------------------------------------
    const liveDidsRef = useRef<Set<string>>(new Set())
    const selectedBlueskyDidRef = useRef<string | null>(selectedBlueskyDid)
    const restoringDidsRef = useRef<Set<string>>(new Set())
    const pendingRevokeRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())
    const initAppliedRef = useRef(false)
    const pendingEventsRef = useRef<
        Map<string, { type: 'expired'; kind: SessionExpiredEvent['kind'] } | { type: 'restored' }>
    >(new Map())
    const isMountedRef = useRef(true)

    // Derive active sessions from selection
    const session = sessions.find(s => s.did === selectedBlueskyDid) ?? sessions[0] ?? null

    // Keep the module-level "live active account" mirror current whenever the
    // active Bluesky session changes. An effect (not a render-time write) so this
    // stays a pure render, per React's rules on side effects (react-hooks/globals).
    useEffect(() => {
        activeBlueskySessionDid = session?.did ?? null
    }, [session?.did])

    const mastodonSession =
        mastodonSessions.find(s => getMastodonAccountId(s) === selectedMastodonId) ??
        mastodonSessions[0] ??
        null

    // Live accounts gate the app shell; expired placeholders still hold their slot.
    const liveAccountCount =
        sessions.length +
        mastodonSessions.length +
        (pixelfedSession ? 1 : 0) +
        (nostrSession ? 1 : 0)
    const totalAccountCount = liveAccountCount + expiredAccounts.length

    const setSelectedBlueskyDidPersisted = useCallback((did: string | null) => {
        selectedBlueskyDidRef.current = did
        setSelectedBlueskyDid(did)
        try {
            if (did) localStorage.setItem(SELECTED_BLUESKY_DID_KEY, did)
            else localStorage.removeItem(SELECTED_BLUESKY_DID_KEY)
        } catch {
            // localStorage unavailable
        }
    }, [])

    /**
     * Turn a confirmed expiry into a placeholder, moving the active selection and
     * clearing caches first when the expired account was the active one.
     */
    const commitExpiry = useCallback(
        async (did: string) => {
            const order = getStoredDids()
            const wasLive = liveDidsRef.current.has(did)
            // Unknown DID that we don't hold and storage doesn't know — not ours.
            if (!wasLive && !order.includes(did)) return

            if (wasLive) {
                liveDidsRef.current.delete(did)

                if (selectedBlueskyDidRef.current === did) {
                    const nextDid = order.find(d => liveDidsRef.current.has(d)) ?? null
                    setSelectedBlueskyDidPersisted(nextDid)
                    queryClient.clear()
                    try {
                        await clearPersistedCache()
                    } catch (err) {
                        // Never leave the UI on a dead account because the persister failed
                        console.error('Failed to clear persisted cache after expiry:', err)
                    }
                }
            }

            if (!isMountedRef.current) return
            dispatch({ type: 'EXPIRED', did, order: getStoredDids() })
        },
        [setSelectedBlueskyDidPersisted]
    )

    /**
     * A cross-tab revoke may just be another tab's reconnect pre-revoke, so the
     * session store — not the message order — decides whether it was really an expiry.
     */
    const verifyThenCommit = useCallback(
        async (did: string) => {
            pendingRevokeRef.current.delete(did)
            const result = await restoreSession(did)
            if (!isMountedRef.current) return
            // 'live' — the record was re-stored (a reconnect); 'transient' — a network
            // blip, which must not destroy caches. Only 'expired' is a real expiry.
            if (result.status === 'expired') await commitExpiry(did)
        },
        [commitExpiry]
    )

    const onExpiredEvent = useCallback(
        ({ did, kind }: SessionExpiredEvent) => {
            if (kind === 'expired') {
                void commitExpiry(did)
                return
            }
            // Provisional: wait for a matching restore, then verify against the store.
            if (!liveDidsRef.current.has(did)) return
            const existing = pendingRevokeRef.current.get(did)
            if (existing) clearTimeout(existing)
            pendingRevokeRef.current.set(
                did,
                setTimeout(() => void verifyThenCommit(did), REVOKE_COALESCE_MS)
            )
        },
        [commitExpiry, verifyThenCommit]
    )

    const handleRestored = useCallback(async (did: string) => {
        const pending = pendingRevokeRef.current.get(did)
        if (pending) {
            clearTimeout(pending)
            pendingRevokeRef.current.delete(did)
        }
        // Already live — an existing OAuthSession reads the rewritten record itself.
        if (liveDidsRef.current.has(did)) return
        if (restoringDidsRef.current.has(did)) return

        restoringDidsRef.current.add(did)
        try {
            const result = await restoreSession(did)
            if (result.status === 'live' && isMountedRef.current) {
                liveDidsRef.current.add(did)
                dispatch({
                    type: 'RESTORED',
                    did,
                    session: result.session,
                    order: getStoredDids(),
                })
            }
        } finally {
            restoringDidsRef.current.delete(did)
        }
    }, [])

    // Subscribe before init: client.restore() fires onDelete for every already-expired
    // DID, so events arriving during init are buffered (last one per DID wins) and
    // replayed once the snapshot has been applied.
    useEffect(() => {
        isMountedRef.current = true
        const timers = pendingRevokeRef.current

        const unsubscribeExpired = subscribeSessionExpired(event => {
            if (!initAppliedRef.current) {
                pendingEventsRef.current.set(event.did, { type: 'expired', kind: event.kind })
                return
            }
            onExpiredEvent(event)
        })
        const unsubscribeRestored = subscribeSessionRestored(did => {
            if (!initAppliedRef.current) {
                pendingEventsRef.current.set(did, { type: 'restored' })
                return
            }
            void handleRestored(did)
        })

        return () => {
            isMountedRef.current = false
            unsubscribeExpired()
            unsubscribeRestored()
            timers.forEach(timer => clearTimeout(timer))
            timers.clear()
        }
    }, [onExpiredEvent, handleRestored])

    useEffect(() => {
        async function init() {
            try {
                await requestPersistentStorage()

                let allBluesky: OAuthSession[] = []
                let expiredDids: string[] = []

                // Skip AT Proto OAuth init during Playwright E2E tests — the OAuth client
                // aggressively clears browser storage on init failure, breaking session injection.
                if (!import.meta.env.VITE_E2E_TEST) {
                    // Restore all sessions stored from previous logins.
                    // initAllSessions() calls client.init() which also processes any
                    // OAuth callback code in the URL, so getSession() is no longer needed.
                    const result = await initAllSessions()
                    allBluesky = result.live
                    expiredDids = result.expiredDids

                    if (
                        result.reconnectOutcome === 'mismatch' ||
                        result.reconnectOutcome === 'rejected'
                    ) {
                        setReconnectError(strings.login.reconnectMismatch)
                    }

                    // Eagerly fetch profile info for any Bluesky sessions without cached data
                    // so the account switcher shows display names instead of raw DIDs.
                    await ensureBlueskyProfilesCached(allBluesky)
                } else {
                    // In E2E tests, allow injecting a minimal fake AT Protocol session
                    // via a localStorage key. All XRPC calls use plain fetch so Playwright
                    // can intercept them. Only used when tests need AT Protocol context.
                    const e2eDid = localStorage.getItem(E2E_BLUESKY_DID_KEY)
                    if (e2eDid) {
                        allBluesky = [
                            {
                                did: e2eDid,
                                fetchHandler(url: string, init: RequestInit) {
                                    return fetch(url, init)
                                },
                            } as unknown as OAuthSession,
                        ]
                    }
                    expiredDids = readE2EExpiredDids().filter(d => d !== e2eDid)
                }

                const allMastodon = getMastodonSessions()

                liveDidsRef.current = new Set(allBluesky.map(s => s.did))
                dispatch({
                    type: 'INIT_SNAPSHOT',
                    live: allBluesky,
                    expiredDids,
                    order: getStoredDids(),
                })
                initAppliedRef.current = true
                setMastodonSessions(allMastodon)

                // Set default selections if not already set, and move off a selection
                // that now points at an expired (or unknown) account.
                const liveDids: string[] = allBluesky.map(s => s.did)
                if (
                    allBluesky.length > 0 &&
                    (!selectedBlueskyDid || !liveDids.includes(selectedBlueskyDid))
                ) {
                    setSelectedBlueskyDidPersisted(allBluesky[0].did)
                }
                if (!selectedMastodonId && allMastodon.length > 0) {
                    const id = getMastodonAccountId(allMastodon[0])
                    setSelectedMastodonId(id)
                    localStorage.setItem(SELECTED_MASTODON_ID_KEY, id)
                }

                const currentPixelfedSession = getPixelfedSession()
                setPixelfedSession(currentPixelfedSession)

                // Replay hook events buffered while the snapshot was being built
                const buffered = pendingEventsRef.current
                pendingEventsRef.current = new Map()
                for (const [did, event] of buffered) {
                    if (event.type === 'expired') onExpiredEvent({ did, kind: event.kind })
                    else void handleRestored(did)
                }
            } catch (err) {
                console.error('Auth initialization error:', err)
                initAppliedRef.current = true
            } finally {
                setIsLoading(false)
            }
        }
        void init()
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: init runs once on mount
    }, [])

    const reconnectBluesky = useCallback(async (did: string) => {
        setReconnectError(null)
        if (import.meta.env.VITE_E2E_TEST) {
            // Never construct the real OAuth client in Playwright
            sessionStorage.setItem(E2E_RECONNECT_DID_KEY, did)
            return true
        }
        return authReconnect(did)
    }, [])

    const clearReconnectError = useCallback(() => setReconnectError(null), [])

    const login = useCallback(async (handle: string) => {
        await authLogin(handle)
    }, [])

    const loginMastodon = useCallback(async (instanceUrl: string) => {
        await mastodonLogin(instanceUrl)
    }, [])

    const setActiveBlueskyDid_ = useCallback(
        (did: string) => {
            setSelectedBlueskyDidPersisted(did)
        },
        [setSelectedBlueskyDidPersisted]
    )

    const setActiveMastodonId_ = useCallback((id: string) => {
        setSelectedMastodonId(id)
        try {
            localStorage.setItem(SELECTED_MASTODON_ID_KEY, id)
        } catch {
            // localStorage unavailable
        }
    }, [])

    const removeAccount = useCallback(
        async (accountId: string) => {
            const isBluesky = accountId.startsWith('did:')

            // Push (#763): unregister while this account still has auth. A no-op
            // unless the flag-gated push hook registered it; never throws.
            await unregisterPushForAccount(accountId, {
                blueskySession: isBluesky ? sessions.find(s => s.did === accountId) : undefined,
                mastodonSession: isBluesky
                    ? undefined
                    : mastodonSessions.find(s => getMastodonAccountId(s) === accountId),
            }).catch(() => undefined)

            if (isBluesky) {
                // Derive the cache key from the DID so an expired account's cached
                // profile is cleared too (it has no live session to look up).
                localStorage.removeItem(`${PROFILE_CACHE_PREFIX}bluesky:${accountId}`)

                if (import.meta.env.VITE_E2E_TEST) {
                    // Never construct the real OAuth client in Playwright
                    removeStoredDid(accountId)
                    e2eForgetBlueskyDid(accountId)
                } else {
                    // Revoke remotely — non-fatal if it fails (network error, etc.)
                    try {
                        await authLogout(accountId)
                    } catch (err) {
                        console.error('Session revocation failed (removing locally):', err)
                    }
                }

                liveDidsRef.current.delete(accountId)
                const pendingRevoke = pendingRevokeRef.current.get(accountId)
                if (pendingRevoke) {
                    clearTimeout(pendingRevoke)
                    pendingRevokeRef.current.delete(accountId)
                }

                const order = getStoredDids()
                dispatch({ type: 'REMOVED', did: accountId, order })

                // Update selection
                if (selectedBlueskyDidRef.current === accountId) {
                    const remainingLive = sessions.filter(
                        s => s.did !== accountId && liveDidsRef.current.has(s.did)
                    )
                    setSelectedBlueskyDidPersisted(remainingLive[0]?.did ?? null)
                }
            } else if (accountId.startsWith('nostr:')) {
                // Clear nostr session
                if (nostrSession) {
                    clearNostrSession(nostrSession)
                }
                setNostrSession(null)
                queryClient.clear()
                await clearPersistedCache()
                return
            } else {
                // Compute remaining sessions before mutating state
                const remaining = mastodonSessions.filter(
                    s => getMastodonAccountId(s) !== accountId
                )

                // Clear profile cache for this account
                const matchingSession = mastodonSessions.find(
                    s => getMastodonAccountId(s) === accountId
                )
                if (matchingSession) {
                    const cacheKey = getProfileCacheKey('mastodon', null, matchingSession)
                    if (cacheKey) localStorage.removeItem(cacheKey)
                }

                removeMastodonStoredSession(accountId)
                evictMastodonServerSession(accountId)
                setMastodonSessions(remaining)

                // Update selection
                if (selectedMastodonId === accountId) {
                    const newId = remaining.length > 0 ? getMastodonAccountId(remaining[0]) : null
                    setSelectedMastodonId(newId)
                    if (newId) {
                        localStorage.setItem(SELECTED_MASTODON_ID_KEY, newId)
                    } else {
                        localStorage.removeItem(SELECTED_MASTODON_ID_KEY)
                    }
                }
            }

            // Always clear query cache, even if remote revocation failed
            queryClient.clear()
            await clearPersistedCache()
        },
        [
            sessions,
            mastodonSessions,
            selectedMastodonId,
            nostrSession,
            setSelectedBlueskyDidPersisted,
        ]
    )

    const loginPixelfed_ = async (instanceUrl: string) => {
        await pixelfedLogin(instanceUrl)
    }

    const loginNostr_ = useCallback(async (method: 'nip07' | 'nsec', nsec?: string) => {
        let pubkey: string
        let secretKey: Uint8Array | null = null

        if (method === 'nip07') {
            const nostrExt = (window as { nostr?: { getPublicKey: () => Promise<string> } }).nostr
            if (!nostrExt) {
                throw new Error('No NIP-07 extension found. Please install Alby or nos2x.')
            }
            pubkey = await nostrExt.getPublicKey()
            if (!/^[0-9a-f]{64}$/.test(pubkey)) {
                throw new Error('NIP-07 extension returned an invalid public key')
            }
        } else {
            if (!nsec) throw new Error('nsec key is required')
            const decoded = nip19.decode(nsec)
            if (decoded.type !== 'nsec') {
                throw new Error('Invalid nsec key. Please provide a valid Nostr secret key.')
            }
            secretKey = decoded.data as Uint8Array
            const { getPublicKey } = await import('nostr-tools')
            pubkey = getPublicKey(secretKey)
        }

        const session: NostrSession = { pubkey, secretKey }

        // Optionally fetch profile metadata
        try {
            const profile = await fetchNostrProfile(pubkey)
            if (profile) {
                session.displayName = profile.display_name ?? profile.name
                session.avatar = profile.picture
            }
        } catch {
            // Profile fetch is non-fatal
        }

        saveNostrSession(session)
        setNostrSession(session)
    }, [])

    const requestNostrReauth_ = useCallback((): Promise<boolean> => {
        return new Promise(resolve => {
            reauthResolverRef.current = resolve
            setShowNostrReauth(true)
        })
    }, [])

    const resolveNostrReauth_ = useCallback((success: boolean) => {
        reauthResolverRef.current?.(success)
        reauthResolverRef.current = null
        setShowNostrReauth(false)
    }, [])

    const logoutNostr_ = useCallback(() => {
        clearNostrSession(nostrSession)
        setNostrSession(null)
    }, [nostrSession])

    const updateMastodonSessionAccount_ = useCallback(
        (accountId: string, account: MastodonSession['account']) => {
            setMastodonSessions(prev => {
                const updated = prev.map(s =>
                    getMastodonAccountId(s) === accountId ? { ...s, account } : s
                )
                updated.forEach(s => {
                    if (getMastodonAccountId(s) === accountId) addMastodonStoredSession(s)
                })
                return updated
            })
        },
        []
    )

    const logoutPixelfed_ = async () => {
        const cacheKey = getProfileCacheKey('pixelfed', null, null, pixelfedSession)
        if (cacheKey) localStorage.removeItem(cacheKey)
        queryClient.clear()
        await clearPersistedCache()
        clearPixelfedSession()
        // Clean up any in-flight PKCE state
        sessionStorage.removeItem('pixelfed_verifier')
        sessionStorage.removeItem('pixelfed_instance')
        sessionStorage.removeItem('pixelfed_state')
        setPixelfedSession(null)
        window.location.reload()
    }

    return (
        <AuthContext.Provider
            value={{
                sessions,
                expiredAccounts,
                blueskyDidOrder,
                mastodonSessions,
                session,
                mastodonSession,
                pixelfedSession,
                nostrSession,
                isLoading,
                // Live-only: an all-expired user is routed to /login, not a dead app shell
                isAuthenticated: liveAccountCount > 0,
                login,
                loginMastodon,
                removeAccount,
                setActiveBlueskyDid: setActiveBlueskyDid_,
                setActiveMastodonId: setActiveMastodonId_,
                loginPixelfed: loginPixelfed_,
                logoutPixelfed: logoutPixelfed_,
                loginNostr: loginNostr_,
                logoutNostr: logoutNostr_,
                showNostrReauth,
                requestNostrReauth: requestNostrReauth_,
                resolveNostrReauth: resolveNostrReauth_,
                totalAccountCount,
                updateMastodonSessionAccount: updateMastodonSessionAccount_,
                reconnectBluesky,
                reconnectError,
                clearReconnectError,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
